import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { COUNTRY_NAMES, fmt, fmtTime } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Users, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Lists() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const navigate = useNavigate();

  const load = async () => setItems((await api.get("/lists")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/lists", form);
      toast.success("Liste créée");
      setOpen(false);
      setForm({ name: "", description: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (l) => {
    await api.delete(`/lists/${l.id}`);
    toast.success("Liste supprimée");
    load();
  };

  return (
    <Layout
      title="Listes"
      subtitle="Regroupez vos contacts, même multi-pays — l'instance reste choisie selon le pays de chaque numéro"
      actions={
        <button onClick={() => setOpen(true)} data-testid="add-list-btn"
          className="rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600 hover:-translate-y-[1px] transition-transform duration-200 flex items-center gap-2">
          <Plus className="h-4 w-4" /> Nouvelle liste
        </button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="lists-page">
        {items.length === 0 && (
          <div className="card-tactical col-span-full p-8 text-center">
            <Users className="h-5 w-5 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Aucune liste. Créez-en une puis assignez des contacts depuis la page Contacts.</p>
          </div>
        )}
        {items.map((l) => (
          <div key={l.id} className="card-tactical p-5" data-testid={`list-card-${l.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-gray-900">{l.name}</p>
                {l.description && <p className="text-xs text-gray-500 mt-0.5">{l.description}</p>}
              </div>
              <button onClick={() => remove(l)} data-testid={`delete-list-${l.id}`}
                className="rounded-md border border-gray-200 p-1.5 hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </button>
            </div>
            <div className="mt-4 flex items-baseline gap-4">
              <div>
                <p className="mono text-3xl font-semibold text-gray-900">{fmt(l.contact_count)}</p>
                <p className="text-xs text-gray-500">contacts</p>
              </div>
              <div>
                <p className="mono text-lg font-semibold text-emerald-700">{fmt(l.active_count)}</p>
                <p className="text-xs text-gray-500">actifs</p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(l.countries || []).map((c) => (
                <span key={c} className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-600">
                  {COUNTRY_NAMES[c] || c}
                </span>
              ))}
            </div>
            <p className="mono mt-3 text-[10px] text-gray-400">{fmtTime(l.created_at)}</p>
            <div className="mt-4 flex gap-2">
              <button onClick={() => navigate(`/contacts?list=${l.id}`)} data-testid={`view-list-contacts-${l.id}`}
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs hover:bg-gray-50">
                Voir les contacts <ArrowRight className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => navigate("/campagnes/nouvelle")} data-testid={`campaign-from-list-${l.id}`}
                className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700 hover:bg-emerald-100">
                Créer une campagne
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-list-dialog">
          <DialogHeader><DialogTitle>Nouvelle liste</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="list-name-input" placeholder="Nom de la liste" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
            <input data-testid="list-description-input" placeholder="Description (optionnel)" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
            <button onClick={create} disabled={!form.name.trim()} data-testid="save-list-btn"
              className="w-full rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-40">
              Créer la liste
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
